import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid2,
  Icon,
  IconButton,
  InputBase,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";

import {
  useGetRelayChannels,
  useSetRelayCommand,
  RelayChannelResponse,
} from "api";
import { Widget } from "components/elements/mui";
import { PageLayout } from "components/layouts";
import { useFormatMessage } from "lang";

const RelayChannelCard = ({
  channel,
  onToggle,
  onRename,
  disabled,
}: {
  channel: RelayChannelResponse;
  onToggle: (channelNumber: number, desiredState: boolean) => void;
  onRename: (channelNumber: number, name: string) => void;
  disabled: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(channel.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleEditClick = () => {
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleNameSave = () => {
    setEditing(false);
    if (nameValue.trim() && nameValue.trim() !== channel.name) {
      onRename(channel.channelNumber, nameValue.trim());
    } else {
      setNameValue(channel.name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleNameSave();
    if (e.key === "Escape") {
      setEditing(false);
      setNameValue(channel.name);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        opacity: channel.hasPendingCommand ? 0.7 : 1,
        transition: "opacity 0.2s",
        "&:hover .edit-btn": { opacity: 1 },
      }}
    >
      <CardContent sx={{ py: "16px !important" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
            >
              {channel.channelNumber}
            </Typography>
            {editing ? (
              <InputBase
                inputRef={inputRef}
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={handleKeyDown}
                sx={{ fontSize: "0.875rem", flex: 1, minWidth: 0 }}
                inputProps={{ maxLength: 64 }}
              />
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                  {channel.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  className="edit-btn"
                  sx={{
                    p: 0,
                    flexShrink: 0,
                    opacity: 0,
                    transition: "opacity 0.2s",
                  }}
                >
                  <Icon sx={{ fontSize: 14 }}>edit</Icon>
                </IconButton>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            {channel.hasPendingCommand && (
              <CircularProgress size={12} thickness={5} />
            )}
            <Switch
              color="success"
              size="small"
              checked={channel.isOn}
              disabled={disabled || channel.hasPendingCommand}
              sx={{ my: 0 }}
              onChange={(_, checked) =>
                onToggle(channel.channelNumber, checked)
              }
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const RelayPage = () => {
  const formatMessage = useFormatMessage();
  const { data: channels, isLoading } = useGetRelayChannels();
  const mutation = useSetRelayCommand();

  const handleToggle = useCallback(
    (channelNumber: number, desiredState: boolean) => {
      mutation.mutate({ channelNumber, desiredState });
    },
    [mutation],
  );

  const handleRename = useCallback(
    (channelNumber: number, name: string) => {
      mutation.mutate({ channelNumber, name });
    },
    [mutation],
  );

  return (
    <PageLayout pageTitle={formatMessage("pageTitle.relay")}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Widget
          size={{ xs: 12 }}
          content={
            <Grid2 container spacing={1.5}>
              {channels?.map((channel) => (
                <Grid2
                  key={channel.channelNumber}
                  size={{ xs: 6, sm: 4, md: 3 }}
                >
                  <RelayChannelCard
                    channel={channel}
                    onToggle={handleToggle}
                    onRename={handleRename}
                    disabled={mutation.isPending}
                  />
                </Grid2>
              ))}
            </Grid2>
          }
        />
      )}
    </PageLayout>
  );
};
